import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Key, Eye, EyeOff, Info, Copy, Check, X, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'

export default function ResourceToken() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [botToken, setBotToken] = useState<string>('')
  const [showToken, setShowToken] = useState<boolean>(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')
  const [copiedToken, setCopiedToken] = useState(false)

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      setBotToken(resource.verification_bot_token || '')
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copiedToken) {
      const timer = setTimeout(() => {
        setCopiedToken(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [copiedToken])

  const handleCopyToken = async () => {
    if (!botToken) return

    try {
      await navigator.clipboard.writeText(botToken)
      setCopiedToken(true)
      hapticFeedback('light')
    } catch (error) {
      console.error('Failed to copy token:', error)
    }
  }

  const validateToken = (token: string): boolean => {
    if (!token.trim()) {
      setError('Токен не может быть пустым')
      return false
    }

    const tokenRegex = /^\d+:[A-Za-z0-9_-]+$/
    if (!tokenRegex.test(token)) {
      setError('Неверный формат токена. Токен должен быть вида: 123456789:ABCdefGHIjklMNOpqrsTUVwxyz')
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!resource) return

    if (!validateToken(botToken)) {
      hapticNotification('error')
      return
    }

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: {
          verification_bot_token: botToken.trim(),
        },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      hideBackButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (err: any) {
      console.error('Failed to update bot token:', err)
      hapticNotification('error')

      // Get error details from API response
      const apiError = err?.response?.data?.error || ''
      const apiDetails = err?.response?.data?.details || ''

      // Map bot verification errors to user-friendly Russian messages
      let errorMessage = 'Ошибка при сохранении токена'

      if (apiError.includes('Bot verification failed')) {
        if (apiDetails.includes('channel not found') || apiDetails.includes('chat not found')) {
          errorMessage = 'Канал не найден. Убедитесь, что Telegram ID указан правильно'
        } else if (apiDetails.includes('invalid verification bot token') || apiDetails.includes('invalid bot token')) {
          errorMessage = 'Неверный токен бота. Проверьте правильность токена'
        } else if (apiDetails.includes('bot not added to channel')) {
          errorMessage = 'Бот не добавлен в канал. Сначала добавьте бот в канал'
        } else if (apiDetails.includes('bot is not channel administrator')) {
          errorMessage = 'Бот не является администратором. Назначьте бот администратором канала'
        } else {
          errorMessage = apiDetails || 'Не удалось проверить статус бота'
        }
      } else if (apiError) {
        errorMessage = apiError
      }

      setError(errorMessage)
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить токен', onSave, '#5288c1', '#ffffff')
    showBackButton(() => {
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`)
    })

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      hideBackButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, showBackButton, hideBackButton, setMainButtonLoading, navigate, resourceId])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Key className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Токен бота</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Token Input - Integration Style */}
        <div className="border-2 border-purple-500/30 rounded-xl bg-gradient-to-r from-purple-500/5 to-pink-500/5 overflow-hidden">
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-bold text-tg-text">Токен бота</h2>
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full">
                    Telegram
                  </span>
                </div>
                <p className="text-xs text-tg-hint">
                  Токен для проверки подписок на канал/группу
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={botToken}
                  onChange={(e) => {
                    setBotToken(e.target.value)
                    setError('')
                  }}
                  onFocus={(e) => {
                    hapticFeedback('light')
                    // Move cursor to the beginning
                    setTimeout(() => {
                      e.target.setSelectionRange(0, 0)
                      e.target.scrollLeft = 0
                    }, 0)
                  }}
                  style={{ touchAction: 'pan-x' }}
                  className="w-full px-4 py-3 pr-28 text-base border-2 border-purple-500/20 rounded-lg bg-tg-secondary-bg text-tg-text placeholder:text-tg-hint focus:border-purple-500 focus:outline-none transition-colors overflow-x-auto"
                  placeholder="123456789:ABCdefGHI..."
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {botToken && (
                    <button
                      onClick={() => {
                        setBotToken('')
                        hapticFeedback('light')
                      }}
                      className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                      title="Очистить"
                    >
                      <X className="w-4 h-4 text-tg-hint" />
                    </button>
                  )}
                  <button
                    onClick={handleCopyToken}
                    className="p-1.5 hover:bg-purple-500/10 rounded-lg transition-colors"
                    title="Копировать токен"
                  >
                    <Copy className={`w-4 h-4 ${copiedToken ? 'text-green-500' : 'text-purple-400'}`} />
                  </button>
                  <button
                    onClick={() => {
                      setShowToken(!showToken)
                      hapticFeedback('light')
                    }}
                    className="p-1.5 hover:bg-purple-500/10 rounded-lg transition-colors"
                    title="Показать/скрыть токен"
                  >
                    {showToken ? (
                      <EyeOff className="w-4 h-4 text-purple-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-purple-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {resource.verification_bot_token && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <Check className="w-3 h-3" />
                      <span>Активен</span>
                    </div>
                  )}
                  {copiedToken && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <Check className="w-3 h-3" />
                      <span>Скопировано</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">Как получить токен бота</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
